import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SortableTable from '../../../../components/common/SortableTable/SortableTable.jsx';
import Pagination from '../../../../components/common/Pagination/Pagination.jsx';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal/ConfirmationModal.jsx';
import { useConfirmation } from '../../../../hooks/useConfirmation';

// Separate search input component to isolate re-renders
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="search-input"
    />
));

// Separate action button to prevent re-renders
const ActionButton = React.memo(({ onClick, text, disabled }) => (
    <button 
        onClick={onClick}
        className="btn btn-primary"
        disabled={disabled}
    >
        {text}
    </button>
));

// Loading overlay component
const LoadingOverlay = React.memo(({ loading }) => 
    loading ? (
        <div className="table-loading-overlay">
            <div className="spinner"></div>
            <span>Loading...</span>
        </div>
    ) : null
);

const SupplierTable = React.memo(({
    hookData,
    onAddSupplier,
    onEditSupplier
}) => {
    const { t } = useTranslation(['inventory', 'common']);
    const { isOpen, confirmationConfig, confirm, closeConfirmation } = useConfirmation();
    const [deletingItemId, setDeletingItemId] = useState(null);

    // Memoized utility functions
    const formatPhone = useCallback((phone) => {
        if (!phone) return '-';
        // Basic phone formatting, can be enhanced based on requirements
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }, []);

    const formatAddress = useCallback((address) => {
        if (!address) return '-';
        // Truncate long addresses for table display
        return address.length > 50 ? `${address.substring(0, 47)}...` : address;
    }, []);

    // Table columns configuration with proper memoization
    const columns = useMemo(() => [
        { 
            key: 'name', 
            label: t('inventory:name'), 
            sortable: true,
            render: (supplier) => <strong>{supplier.name}</strong>
        },
        { 
            key: 'contact_person', 
            label: t('inventory:contact'), 
            sortable: true,
            render: (supplier) => supplier.contact_person || '-'
        },
        {
            key: 'email',
            label: t('inventory:email'),
            sortable: true,
            render: (supplier) => supplier.email ? (
                <a href={`mailto:${supplier.email}`} className="email-link">
                    {supplier.email}
                </a>
            ) : '-'
        },
        {
            key: 'phone',
            label: t('inventory:phone'),
            sortable: true,
            render: (supplier) => formatPhone(supplier.phone)
        },
        {
            key: 'address',
            label: t('inventory:address'),
            sortable: false,
            render: (supplier) => (
                <span title={supplier.address || ''}>
                    {formatAddress(supplier.address)}
                </span>
            )
        },
        {
            key: 'is_active',
            label: t('inventory:status'),
            sortable: true,
            render: (supplier) => (
                <span className={`status-badge ${supplier.is_active ? 'active' : 'inactive'}`}>
                    {supplier.is_active ? t('common:active') : t('common:inactive')}
                </span>
            )
        }
    ], [t, formatPhone, formatAddress]);

    // Handle delete with confirmation
    const handleDeleteClick = useCallback(async (supplier) => {
        const confirmed = await confirm({
            title: t('common:confirmation'),
            message: t('inventory:confirmDeleteSupplier', { name: supplier.name }),
            confirmText: t('common:buttons.delete'),
            confirmButtonClass: 'btn-danger'
        });

        if (confirmed) {
            setDeletingItemId(supplier.id);
            await hookData.deleteSupplier(supplier.id);
            setDeletingItemId(null);
        }
    }, [confirm, t, hookData]);

    // Get row styling based on supplier status
    const getRowClassName = useCallback((supplier) => {
        return !supplier.is_active ? 'inactive-supplier' : '';
    }, []);

    // Memoized pagination props
    const paginationProps = useMemo(() => ({
        pagination: hookData.pagination,
        onPageChange: hookData.onPageChange,
        onLimitChange: hookData.onLimitChange,
        limitOptions: hookData.limitOptions
    }), [
        hookData.pagination,
        hookData.onPageChange,
        hookData.onLimitChange,
        hookData.limitOptions
    ]);

    return (
        <div className="inventory-content">
            {/* Search and Action Bar - isolated components */}
            <div className="action-bar">
                <SearchInput
                    value={hookData.search}
                    onChange={hookData.onSearchChange}
                    placeholder={t('inventory:searchSuppliers')}
                />
                <ActionButton
                    onClick={onAddSupplier}
                    text={`${t('common:add')} ${t('inventory:supplier')}`}
                    disabled={hookData.actionLoading}
                />
            </div>

            {/* Table with loading overlay - isolated re-renders */}
            <div style={{ position: 'relative' }}>
                <SortableTable
                    columns={columns}
                    data={hookData.suppliers}
                    onEdit={onEditSupplier}
                    onDelete={handleDeleteClick}
                    getRowClassName={getRowClassName}
                    sortField={hookData.sort}
                    sortOrder={hookData.order}
                    onSort={hookData.onSortChange}
                    emptyMessage={hookData.search ? t('inventory:noSuppliersSearch') : t('inventory:noSuppliersFound')}
                />
                <LoadingOverlay loading={hookData.loading} />
            </div>

            {/* Pagination Controls - memoized */}
            {hookData.pagination && (
                <Pagination {...paginationProps} />
            )}

            <ConfirmationModal
                isOpen={isOpen}
                onClose={closeConfirmation}
                onConfirm={confirmationConfig.onConfirm}
                title={confirmationConfig.title}
                message={confirmationConfig.message}
                confirmText={confirmationConfig.confirmText}
                cancelText={confirmationConfig.cancelText}
                confirmButtonClass={confirmationConfig.confirmButtonClass}
                loading={deletingItemId !== null}
            />
        </div>
    );
});

// Display name for React DevTools
SupplierTable.displayName = 'SupplierTable';

export default SupplierTable;